import * as React from 'react';
import { Dialog, DialogType } from '@fluentui/react/lib/Dialog';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { ChoiceGroup } from '@fluentui/react/lib/ChoiceGroup';
import { Icon, mergeStyles, TextField, WindowProvider } from '@fluentui/react';
import * as ReactDOM from 'react-dom';
import { justificationChangeButtonId, justificationCancelButtonId, justificationTextboxId } from './utilities/testConstants';
import { getFFXHostUxIcon } from '@fluidx/icons/lib/useIconNames/ffxHostUx';
let currentTarget;
const clearCurrentTarget = ()=>{
    if (currentTarget !== undefined) {
        ReactDOM.unmountComponentAtNode(currentTarget);
        currentTarget.remove();
        currentTarget = undefined;
    }
};
export function ShowJustificationDialog(informationProtectionStringTable, updateJustificationCallback, moreInfoUrl, styles, stylingProviderThemeProperties, ownerDocument) {
    const doc = ownerDocument || document;
    // Create a render element required by ReactDOM render
    currentTarget = doc.createElement('div');
    ReactDOM.render(/*#__PURE__*/ React.createElement(WindowProvider, {
        window: doc.defaultView || undefined
    }, /*#__PURE__*/ React.createElement(SensitivityJustificationDialog, {
        informationProtectionStringTable: informationProtectionStringTable,
        updateJustificationCallback: updateJustificationCallback,
        moreInfoUrl: moreInfoUrl,
        styles: styles,
        stylingProviderThemeProperties: stylingProviderThemeProperties,
        onDismissed: clearCurrentTarget
    })), currentTarget);
}
/**
 * This control is invoked if changing the sensitivity label requires downgrade justification. This
 * control ensures to collect the user justification and appropriately invokes the setSensitivityLabel
 * API.
 */ export const SensitivityJustificationDialog = /*#__PURE__*/ React.memo((props)=>{
    const { informationProtectionStringTable , updateJustificationCallback , moreInfoUrl , styles , stylingProviderThemeProperties , onDismissed  } = props;
    const [disableJustificationText, setDisableJustificationText] = React.useState(true);
    const [disableChangeButton, setDisableChangeButton] = React.useState(true);
    const [hideDialog, setHideDialog] = React.useState(false);
    // The default option if the choice group is NotApplicable
    const [justificationText, setJustificationText] = React.useState('Previous label no longer applies');
    const options = React.useMemo(()=>{
        return [
            {
                key: 'NotApplicable',
                text: informationProtectionStringTable.justificationOptionNotApplicableText
            },
            {
                key: 'Incorrect',
                text: informationProtectionStringTable.justificationOptionIncorrectText
            },
            {
                key: 'Other',
                text: informationProtectionStringTable.justificationOptionOtherText
            }
        ];
    }, [
        informationProtectionStringTable
    ]);
    const dialogContentProps = React.useMemo(()=>{
        return {
            type: DialogType.normal,
            title: informationProtectionStringTable.justificationTitle,
            subText: informationProtectionStringTable.justificationSubText
        };
    }, [
        informationProtectionStringTable
    ]);
    const modalProps = {
        isBlocking: true,
        className: mergeStyles(stylingProviderThemeProperties),
        styles: styles?.modal,
        onDismissed
    };
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any
-- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */ const onJustificationChange = React.useCallback((_ev, option)=>{
        if (!option) {
            return;
        }
        // If the user chooses to enter justification text then
        // enable the textbox and disable the change button until
        // the user enters the text.
        if (option.key === 'Other') {
            setDisableJustificationText(false);
            setDisableChangeButton(true);
        } else {
            setJustificationText(option.text);
            setDisableJustificationText(true);
            setDisableChangeButton(false);
        }
    }, []);
    const onChangeOtherTextValue = React.useCallback((_event, newValue)=>{
        if (newValue && newValue.length > 0) {
            setJustificationText(newValue);
            setDisableChangeButton(false);
        } else {
            setDisableChangeButton(true);
        }
    }, []);
    const onChangeClicked = React.useCallback(()=>{
        // TODO: Error if Justification text is not available
        if (justificationText) {
            setHideDialog(true);
            updateJustificationCallback(justificationText);
        }
    }, [
        justificationText,
        updateJustificationCallback
    ]);
    const onCancelClicked = React.useCallback(()=>{
        setHideDialog(true);
    }, []);
    const onLearnMoreClicked = React.useMemo(()=>{
        return moreInfoUrl ? ()=>{
            window.open(moreInfoUrl);
        } : undefined;
    }, [
        moreInfoUrl
    ]);
    const LearnMoreIconName = getFFXHostUxIcon('FFXCOpen');
    return /*#__PURE__*/ React.createElement(Dialog, {
        hidden: hideDialog,
        onDismiss: onCancelClicked,
        dialogContentProps: dialogContentProps,
        modalProps: modalProps
    }, /*#__PURE__*/ React.createElement(ChoiceGroup, {
        options: options,
        onChange: onJustificationChange,
        styles: styles?.choiceGroup
    }), /*#__PURE__*/ React.createElement(TextField, {
        "data-testid": justificationTextboxId,
        placeholder: informationProtectionStringTable.justificationPlaceholderText,
        autoAdjustHeight: true,
        onChange: onChangeOtherTextValue,
        disabled: disableJustificationText,
        styles: styles?.textField
    }), /*#__PURE__*/ React.createElement("div", {
        style: styles?.buttonWrapper
    }, /*#__PURE__*/ React.createElement(PrimaryButton, {
        "data-testid": justificationChangeButtonId,
        onClick: onChangeClicked,
        text: informationProtectionStringTable.justificationChangeButtonText,
        disabled: disableChangeButton,
        styles: styles?.primaryButton
    }), /*#__PURE__*/ React.createElement(DefaultButton, {
        "data-testid": justificationCancelButtonId,
        onClick: onCancelClicked,
        text: informationProtectionStringTable.justificationCancelButtonText,
        styles: styles?.defaultButton
    })), onLearnMoreClicked ? /*#__PURE__*/ React.createElement("p", {
        className: mergeStyles(styles?.footerText)
    }, informationProtectionStringTable.justificationFooterText, ' ', /*#__PURE__*/ React.createElement("button", {
        onClick: onLearnMoreClicked,
        className: mergeStyles(styles?.footerLink),
        role: "link"
    }, informationProtectionStringTable.justificationLearnMoreLinkText, " ", /*#__PURE__*/ React.createElement(Icon, {
        iconName: LearnMoreIconName
    }))) : null);
});
